import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Power } from 'lucide-react';
import FEATURE_KEYS from '../../../../constants/feature-keys';
import type { Facility } from '../../utils/utils';
import {
    deriveFacilityStatus,
    getDaysRemaining,
    getFacilityAdoptionPercent,
    isFacilityDeactivated,
} from '../../utils/utils';
import DeactivatedBadge from '../cells/DeactivatedBadge';
import FacilityIdentityCell from '../cells/FacilityIdentityCell';
import LicenseCell from '../cells/LicenseCell';
import type {
    FacilityColumn,
    StageFilterOption,
    StageSortOption,
} from '../utils/facilityTableTypes';

interface CreateColumnsOptions {
    onDeactivate?: (facility: Facility) => void;
}

export const trialFilterOptions: StageFilterOption[] = [
    { value: 'all', label: 'All' },
    { value: 'adoption-ready', label: 'Adoption Ready (75-100%)' },
    { value: 'at-risk', label: 'At Risk (50-74%)' },
    { value: 'low-probability', label: 'Low Probability (<50%)' },
    { value: 'expiring', label: 'Expiring this week' },
    { value: 'deactivated', label: 'Deactivated' },
];

export const trialSortOptions: StageSortOption[] = [];

export function createTrialColumns({
    onDeactivate,
}: CreateColumnsOptions): FacilityColumn[] {
    return [
        {
            key: 'facility',
            header: 'Facility',
            className: 'w-[24%]',
            cell: (f) => <FacilityIdentityCell facility={f} />,
        },
        {
            key: 'expiry',
            header: 'Expiry date',
            className: 'w-[18%]',
            cell: (f) => <ExpiryDateCell facility={f} />,
        },
        {
            key: 'users',
            header: 'Users',
            className: 'w-[12%]',
            cell: (f) => <LicenseCell facility={f} />,
        },
        {
            key: 'adoption',
            header: 'Adoption',
            className: 'w-[16%]',
            cell: (f) => <AdoptionCell facility={f} />,
        },
        {
            key: 'cpt',
            header: 'CPT',
            className: 'w-[10%]',
            cell: (f) => (
                <span className="text-xs tabular-nums text-muted-foreground">
                    {f.featureAccess?.includes(FEATURE_KEYS.cptReporting)
                        ? (f.reportCount ?? 0)
                        : '—'}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            className: 'w-[12%]',
            cell: (f) => <TrialStatusCell facility={f} />,
        },
        {
            key: 'actions',
            header: '',
            className: 'w-[8%] text-right',
            headerClassName: 'text-right',
            cell: (f) => (
                <div className="flex justify-end">
                    {isFacilityDeactivated(f) ? (
                        <DeactivatedBadge />
                    ) : (
                        onDeactivate && (
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeactivate(f);
                                }}
                            >
                                <Power
                                    className="mr-1 h-3 w-3"
                                    aria-hidden="true"
                                />
                                Deactivate
                            </Button>
                        )
                    )}
                </div>
            ),
        },
    ];
}

function ExpiryDateCell({ facility }: { facility: Facility }) {
    if (isFacilityDeactivated(facility)) {
        return <span className="text-xs text-muted-foreground">—</span>;
    }

    const days = getDaysRemaining(
        facility.start_date,
        'trial',
        facility.end_date
    );
    const dateLabel = facility.end_date
        ? new Date(facility.end_date).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
          })
        : 'Not set';

    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground">{dateLabel}</span>
            <ExpiryBadge days={days} />
        </div>
    );
}

function ExpiryBadge({ days }: { days: number | null }) {
    if (days === null) {
        return (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Not set
            </span>
        );
    }

    const label =
        days < 0
            ? `${Math.abs(days)}d ago`
            : days === 0
              ? 'Today'
              : `${days}d left`;
    const className =
        days <= 7
            ? 'border-amber-300/15 bg-amber-300/5 text-amber-200'
            : 'border-white/10 bg-white/5 text-muted-foreground';

    return (
        <span
            className={`rounded-full border px-2 py-0.5 text-xs font-medium tabular-nums ${className}`}
        >
            {label}
        </span>
    );
}

function AdoptionCell({ facility }: { facility: Facility }) {
    const pct = getFacilityAdoptionPercent(facility);

    return (
        <div className="min-w-20">
            <span className="text-xs font-medium tabular-nums text-foreground">
                {pct}%
            </span>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/5">
                <div
                    className="h-full rounded-full bg-zinc-300"
                    style={{ width: `${Math.min(pct, 100)}%` }}
                />
            </div>
        </div>
    );
}

function TrialStatusCell({ facility }: { facility: Facility }) {
    if (isFacilityDeactivated(facility)) {
        return <span className="text-xs text-muted-foreground">—</span>;
    }

    const status = deriveFacilityStatus(facility);

    if (status === 'trial-expired') {
        return (
            <Badge
                variant="outline"
                className="rounded-full border-amber-300/15 bg-amber-300/5 px-2 py-0.5 text-xs font-medium text-amber-200"
            >
                Expired
            </Badge>
        );
    }

    return (
        <Badge
            variant="outline"
            className="rounded-full border-white/10 bg-white/5 px-2 py-0.5 text-xs font-medium text-muted-foreground"
        >
            Trial
        </Badge>
    );
}
